import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ScribeMicrophone } from '@/shared/voice/scribe.service';

import { fieldErrorMessage } from '../field-view';

import { fieldSpeechStream, fieldVoiceToken } from './field-client';
import { FieldSpeechPlayer } from './field-speech-player';

import type { FieldActorId, FieldMessage } from '@mio/shared';

interface FieldVoiceOptions {
  /** Saved speech and asynchronous call results belong only to this engagement. */
  engagementId?: string | undefined;
  /** The current conversation has an accepted message awaiting a colleague response. */
  awaitingReply?: boolean;
  /** Another command is committing. Speech waits locally instead of disappearing. */
  inputBlocked?: boolean;
}

const TURN_SETTLE_MS = 250;

/** Field calls only deliver committed transcripts to the selected colleague or meeting. */
export function useFieldVoice(
  actorId: FieldActorId,
  messages: FieldMessage[],
  onTranscript: (text: string) => Promise<boolean>,
  options: FieldVoiceOptions = {},
) {
  const [connection, setConnection] = useState<'off' | 'connecting' | 'ready'>('off');
  const [playback, setPlayback] = useState<'idle' | 'preparing' | 'speaking'>('idle');
  const [sending, setSending] = useState(false);
  const [waitingForReply, setWaitingForReply] = useState(false);
  const [muted, setMuted] = useState(false);
  const [partial, setPartial] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [canReplay, setCanReplay] = useState(false);
  const [unsent, setUnsent] = useState<Partial<Record<FieldActorId, string>>>({});
  const [turnReady, setTurnReady] = useState(false);
  const mic = useRef<ScribeMicrophone | null>(null);
  const player = useRef<FieldSpeechPlayer | null>(null);
  const controller = useRef<AbortController | null>(null);
  const active = useRef(false);
  const sessionEpoch = useRef(0);
  const engagement = useRef(options.engagementId);
  const engagementEpoch = useRef(0);
  const sendingRef = useRef(false);
  const seen = useRef(new Set<string>());
  const supersededOperations = useRef(new Set<string>());
  const replayMessage = useRef<FieldMessage | null>(null);
  const pendingTurn = useRef<{
    actorId: FieldActorId;
    text: string;
    supersedesReply: boolean;
  } | null>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const callback = useRef(onTranscript);
  callback.current = onTranscript;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const preserve = useCallback((actor: FieldActorId, text: string, scope: number) => {
    if (scope !== engagementEpoch.current || !text.trim()) return;
    setUnsent((previous) => ({
      ...previous,
      [actor]: `${previous[actor] ?? ''} ${text}`.trim(),
    }));
  }, []);

  const stopAudio = useCallback(() => {
    controller.current?.abort();
    controller.current = null;
    setPlayback('idle');
    if (active.current && replayMessage.current) setCanReplay(true);
  }, []);

  const stop = useCallback(() => {
    active.current = false;
    sessionEpoch.current += 1;
    clearTimeout(settleTimer.current);
    if (pendingTurn.current) {
      preserve(pendingTurn.current.actorId, pendingTurn.current.text, engagementEpoch.current);
      pendingTurn.current = null;
    }
    setTurnReady(false);
    mic.current?.stop();
    mic.current = null;
    stopAudio();
    player.current?.dispose();
    player.current = null;
    sendingRef.current = false;
    setSending(false);
    setWaitingForReply(false);
    setConnection('off');
    setPartial('');
    setMuted(false);
    setCanReplay(false);
    replayMessage.current = null;
  }, [preserve, stopAudio]);

  useLayoutEffect(() => {
    if (engagement.current === options.engagementId) return;
    engagement.current = options.engagementId;
    engagementEpoch.current += 1;
    // Discard the prior scope before stop can preserve it, and before passive send effects run.
    pendingTurn.current = null;
    stop();
    setUnsent({});
    setError(null);
    seen.current = new Set();
    supersededOperations.current = new Set();
  }, [options.engagementId, stop]);

  useEffect(() => {
    setError(null);
    return stop;
  }, [actorId, stop]);

  const start = async () => {
    if (active.current) return;
    setError(null);
    setConnection('connecting');
    active.current = true;
    const epoch = ++sessionEpoch.current;
    const scope = engagementEpoch.current;
    const isCurrent = () =>
      active.current && sessionEpoch.current === epoch && engagementEpoch.current === scope;
    const settleTurn = () => {
      clearTimeout(settleTimer.current);
      setTurnReady(false);
      if (pendingTurn.current)
        settleTimer.current = setTimeout(() => {
          if (isCurrent()) setTurnReady(true);
        }, TURN_SETTLE_MS);
    };
    seen.current = new Set(messagesRef.current.map((message) => message.id));
    supersededOperations.current = new Set();
    const capture = new ScribeMicrophone({
      onPartial: (text) => {
        if (!isCurrent()) return;
        setPartial(text);
        // Raw amplitude can be the colleague's own audio leaking through speakers. Yield only
        // for a recognisable utterance, after the browser's microphone echo cancellation.
        if (text.trim().split(/\s+/).length >= 2 || /^(stop|wait|hold|no)\b/i.test(text.trim()))
          stopAudio();
        if (pendingTurn.current && text.trim()) {
          clearTimeout(settleTimer.current);
          setTurnReady(false);
        } else if (!text.trim()) settleTurn();
      },
      onCommit: (text) => {
        if (!isCurrent()) return;
        setPartial('');
        if (!text.trim()) {
          settleTurn();
          return;
        }
        stopAudio();
        pendingTurn.current = {
          actorId,
          text: `${pendingTurn.current?.text ?? ''} ${text.trim()}`.trim(),
          supersedesReply:
            pendingTurn.current?.supersedesReply === true ||
            optionsRef.current.awaitingReply === true ||
            sendingRef.current,
        };
        // Scribe can commit adjacent fragments of one sentence. Collect them as one turn.
        settleTurn();
      },
      onActivity: () => {
        // Microphone amplitude alone never interrupts a colleague's answer.
      },
      onInterrupted: () => {
        if (!isCurrent()) return;
        setError('Your browser paused microphone capture. Reconnect voice to continue.');
        stop();
      },
      onError: (message) => {
        if (!isCurrent()) return;
        setError(message);
        stop();
      },
    });
    mic.current = capture;
    try {
      player.current = new FieldSpeechPlayer();
      player.current.unlock();
      await capture.prepare();
      if (!isCurrent()) return;
      const token = await fieldVoiceToken();
      if (!isCurrent()) return;
      await capture.start(token.token);
      if (isCurrent()) setConnection('ready');
    } catch (cause) {
      if (!isCurrent()) return;
      setError(fieldErrorMessage(cause));
      stop();
    }
  };

  useEffect(() => {
    if (
      !active.current ||
      connection !== 'ready' ||
      !turnReady ||
      !pendingTurn.current ||
      sendingRef.current ||
      options.awaitingReply ||
      options.inputBlocked
    )
      return;
    const turn = pendingTurn.current;
    const epoch = sessionEpoch.current;
    const scope = engagementEpoch.current;
    const supersededOperationId = turn.supersedesReply
      ? messagesRef.current.findLast(
          (message) =>
            message.role === 'learner' &&
            message.actorId === turn.actorId &&
            message.operationId !== null,
        )?.operationId
      : null;
    pendingTurn.current = null;
    setTurnReady(false);
    sendingRef.current = true;
    setSending(true);
    setError(null);
    void callback
      .current(turn.text)
      .then((delivered) => {
        if (engagementEpoch.current !== scope) return;
        if (!delivered) {
          preserve(turn.actorId, turn.text, scope);
          if (active.current && sessionEpoch.current === epoch)
            setError('Your last turn was not sent. Your words are saved below.');
        } else if (active.current && sessionEpoch.current === epoch) {
          // A follow-up changes the turn being answered. Keep the previous answer in the
          // transcript, but do not start its audio after the follow-up has been accepted.
          // Match its operation, rather than dropping unrelated colleague announcements.
          if (supersededOperationId) supersededOperations.current.add(supersededOperationId);
          // Callers with operation state provide the authoritative waiting state.
          setWaitingForReply(optionsRef.current.awaitingReply === undefined);
        }
      })
      .catch(() => {
        if (engagementEpoch.current !== scope) return;
        preserve(turn.actorId, turn.text, scope);
        if (active.current && sessionEpoch.current === epoch)
          setError('Your last turn was not sent. Your words are saved below.');
      })
      .finally(() => {
        if (!active.current || sessionEpoch.current !== epoch) return;
        sendingRef.current = false;
        setSending(false);
      });
  }, [connection, options.awaitingReply, options.inputBlocked, preserve, sending, turnReady]);

  const speak = useCallback((message: FieldMessage) => {
    if (!active.current || controller.current || !player.current) return;
    const epoch = sessionEpoch.current;
    const request = new AbortController();
    controller.current = request;
    replayMessage.current = message;
    setCanReplay(false);
    setError(null);
    setWaitingForReply(false);
    setPlayback('preparing');
    const playbackPlayer = player.current;
    void fieldSpeechStream(message.id, request.signal)
      .then((response) => {
        if (request.signal.aborted || !active.current || sessionEpoch.current !== epoch) return;
        return playbackPlayer.play(response, request.signal, () => {
          if (!request.signal.aborted && active.current && sessionEpoch.current === epoch)
            setPlayback('speaking');
        });
      })
      .catch((cause: unknown) => {
        if (request.signal.aborted || !active.current || sessionEpoch.current !== epoch) return;
        setError(
          cause instanceof DOMException && cause.name === 'NotAllowedError'
            ? 'Your browser paused audio. Choose Play reply to hear it.'
            : `${fieldErrorMessage(cause)} The complete reply is in the conversation.`,
        );
        setCanReplay(true);
      })
      .finally(() => {
        if (controller.current !== request) return;
        controller.current = null;
        setPlayback('idle');
      });
  }, []);

  useEffect(() => {
    if (
      !active.current ||
      connection !== 'ready' ||
      controller.current ||
      sendingRef.current ||
      sending ||
      partial.trim() ||
      pendingTurn.current
    )
      return;
    const next = messages.find(
      (message) =>
        message.actorId === actorId &&
        message.role === 'colleague' &&
        !seen.current.has(message.id) &&
        (!message.operationId || !supersededOperations.current.has(message.operationId)),
    );
    if (!next) return;
    seen.current.add(next.id);
    speak(next);
  }, [messages, actorId, connection, playback, partial, sending, speak, turnReady]);

  const status =
    connection === 'off'
      ? 'off'
      : connection === 'connecting'
        ? 'connecting'
        : playback === 'speaking'
          ? 'speaking'
          : playback === 'preparing' || sending || waitingForReply || options.awaitingReply
            ? 'thinking'
            : 'listening';

  return {
    status,
    muted,
    partial,
    error,
    canReplay,
    replay: () => {
      if (replayMessage.current && canReplay) speak(replayMessage.current);
    },
    unsent: unsent[actorId] ?? '',
    clearUnsent: () => setUnsent((previous) => ({ ...previous, [actorId]: '' })),
    start,
    stop,
    stopAudio,
    toggleMute: () =>
      setMuted((value) => {
        mic.current?.mute(!value);
        return !value;
      }),
  };
}
